<script setup>
import { reactive, computed, watch, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import generatePayload from 'dashboard/helper/filterQueryGenerator.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import CampaignAudienceFilter from 'dashboard/components-next/filter/CampaignAudienceFilter.vue';
import CampaignSegmentManager from 'dashboard/components-next/filter/CampaignSegmentManager.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useCampaignFilterContext } from 'dashboard/components-next/filter/campaignProvider.js';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();
const { filterTypes } = useCampaignFilterContext();

// Build lookup map: attributeKey -> customAttributeType
const customAttributeTypeMap = computed(() => {
  const map = {};
  filterTypes.value.forEach(ft => {
    if (ft.customAttributeType) {
      map[ft.attributeKey] = ft.customAttributeType;
    }
  });
  return map;
});

// Enrich filters with custom_attribute_type before sending to backend
const enrichFilters = filters => {
  return filters.map(f => ({
    ...f,
    custom_attribute_type: customAttributeTypeMap.value[f.attributeKey] || '',
  }));
};

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  audienceFilters: [
    {
      attributeKey: 'phone_number',
      filterOperator: 'is_present',
      values: [],
      queryOperator: 'and',
      attributeModel: 'standard',
    },
  ],
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);
const audienceFilterRef = ref(null);
const eligibleCount = ref(null);
const eligibleSample = ref([]);
const isFetchingCount = ref(false);
const audienceCountError = ref(false);
let debounceTimer = null;
let audienceCountRequestId = 0;

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.isFormInvalid === false;
});

const hasZeroEligible = computed(() => eligibleCount.value === 0);
const hasTooManyFilters = computed(() => state.audienceFilters.length > 5);
const hasLargeAudience = computed(() => eligibleCount.value > 1000);

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParams.value ||
    hasZeroEligible.value ||
    eligibleCount.value === null ||
    isFetchingCount.value ||
    audienceCountError.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const fetchAudienceCount = async () => {
  audienceCountRequestId += 1;
  const requestId = audienceCountRequestId;

  if (!state.audienceFilters || state.audienceFilters.length === 0) {
    eligibleCount.value = null;
    eligibleSample.value = [];
    audienceCountError.value = false;
    return;
  }

  const enriched = enrichFilters(state.audienceFilters);
  const payload = generatePayload(
    useSnakeCase(JSON.parse(JSON.stringify(enriched)))
  );
  isFetchingCount.value = true;
  audienceCountError.value = false;
  try {
    const result = await store.dispatch(
      'campaigns/fetchAudienceCount',
      payload
    );
    if (requestId !== audienceCountRequestId) return;

    eligibleCount.value = result.count;
    eligibleSample.value = result.sample || [];
  } catch {
    if (requestId !== audienceCountRequestId) return;

    eligibleCount.value = null;
    eligibleSample.value = [];
    audienceCountError.value = true;
  } finally {
    if (requestId === audienceCountRequestId) {
      isFetchingCount.value = false;
    }
  }
};

const debouncedFetchCount = () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fetchAudienceCount, 500);
};

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
  audienceCountRequestId += 1;
});

watch(() => state.audienceFilters, debouncedFetchCount, {
  deep: true,
  immediate: true,
});

const resetState = () => {
  audienceCountRequestId += 1;
  Object.assign(state, initialState);
  v$.value.$reset();
  eligibleCount.value = null;
  eligibleSample.value = [];
  audienceCountError.value = false;
  isFetchingCount.value = false;
};

const handleCancel = () => emit('cancel');

const handleLoadSegment = filters => {
  state.audienceFilters = filters;
};

const prepareCampaignDetails = () => {
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;
  const templateContent = parserData?.renderedTemplate || '';

  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  const enriched = enrichFilters(state.audienceFilters);
  const audienceFilters = generatePayload(
    useSnakeCase(JSON.parse(JSON.stringify(enriched)))
  );

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience_filters: audienceFilters,
    ...(parserData?.mediaBlobId
      ? { header_media: parserData.mediaBlobId }
      : {}),
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !hasRequiredTemplateParams.value) return;

  if (
    eligibleCount.value === null ||
    isFetchingCount.value ||
    audienceCountError.value ||
    eligibleCount.value === 0
  )
    return;

  // Confirmation for large audiences
  if (eligibleCount.value > 1000) {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Você está prestes a enviar para ${eligibleCount.value} contatos. Deseja continuar?`
    );
    if (!confirmed) return;
  }

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
      media-upload
    />

    <!-- Audience Filters -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') ||
          'Audience Filters'
        }}
      </label>
      <CampaignAudienceFilter
        ref="audienceFilterRef"
        v-model="state.audienceFilters"
      />

      <CampaignSegmentManager
        :current-filters="state.audienceFilters"
        @load-segment="handleLoadSegment"
      />

      <!-- Eligible count display -->
      <div
        v-if="eligibleCount !== null"
        class="flex items-center gap-2 px-3 py-2 rounded-lg"
        :class="{
          'bg-n-ruby-3 text-n-ruby-11': eligibleCount === 0,
          'bg-n-green-3 text-n-green-11': eligibleCount > 0,
        }"
      >
        <Spinner v-if="isFetchingCount" :size="16" />
        <span class="text-sm font-medium">
          {{ eligibleCount }}
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ELIGIBLE') ||
            'eligible contacts'
          }}
        </span>
      </div>

      <p
        v-if="isFetchingCount && eligibleCount === null"
        class="text-sm text-n-slate-11"
      >
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.COUNT_LOADING') }}
      </p>

      <!-- Zero eligible warning -->
      <p v-if="eligibleCount === 0" class="text-sm text-n-ruby-11">
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ZERO_ELIGIBLE') ||
          'No eligible contacts for the selected filters'
        }}
      </p>

      <p v-if="audienceCountError" class="text-sm text-n-ruby-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.COUNT_ERROR') }}
      </p>

      <!-- Too many filters warning -->
      <p v-if="hasTooManyFilters" class="text-sm text-n-amber-11">
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.TOO_MANY_FILTERS') ||
          'Muitos filtros podem afetar a performance da busca'
        }}
      </p>

      <!-- Large audience warning -->
      <p
        v-if="hasLargeAudience && !hasZeroEligible"
        class="text-sm text-n-amber-11"
      >
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LARGE_AUDIENCE') ||
          'Você será solicitado a confirmar antes do envio'
        }}
      </p>

      <!-- Sample preview -->
      <div
        v-if="eligibleSample.length > 0"
        class="overflow-hidden border rounded-lg border-n-weak"
      >
        <table class="w-full text-sm">
          <thead class="bg-n-slate-3">
            <tr>
              <th class="px-3 py-1.5 text-left text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PREVIEW_NAME') }}
              </th>
              <th class="px-3 py-1.5 text-left text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PREVIEW_PHONE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="contact in eligibleSample.slice(0, 5)"
              :key="contact.id"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-1.5 text-n-slate-12">{{ contact.name }}</td>
              <td class="px-3 py-1.5 text-n-slate-11">
                {{ contact.phone_number }}
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-if="eligibleSample.length > 5"
          class="px-3 py-1 text-xs text-center text-n-slate-10 bg-n-slate-2"
        >
          + {{ eligibleCount - 5 }}
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.MORE_CONTACTS') }}
        </div>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
